function publishValesstraCRO(eventName, detail = {}) {
  const payload = {
    ...detail,
    event_time: Date.now(),
  };

  window.dispatchEvent(new CustomEvent(eventName, { detail: payload }));

  if (window.Shopify?.analytics?.publish) {
    try {
      window.Shopify.analytics.publish(eventName, payload);
    } catch (error) {
      console.debug('Valesstra CRO event publish skipped', error);
    }
  }

  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event: eventName, ...payload });
  }
}

function initValesstraCROTracking() {
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.closest('.valesstra-complete-look')) return;

      let sourceInput = form.querySelector('input[name="properties[_valesstra_source]"]');
      if (!sourceInput) {
        sourceInput = document.createElement('input');
        sourceInput.type = 'hidden';
        sourceInput.name = 'properties[_valesstra_source]';
        form.appendChild(sourceInput);
      }
      sourceInput.value = 'complete_the_look';

      publishValesstraCRO('valesstra_complete_the_look_add', {
        source: 'complete_the_look',
        variant_id: form.querySelector('[name="id"]')?.value || null,
        product_path: window.location.pathname,
      });
    },
    true
  );

  document.addEventListener('click', (event) => {
    const completeLookLink = event.target.closest(
      '.valesstra-complete-look .card__heading a, .valesstra-complete-look .card__media a'
    );

    if (completeLookLink) {
      publishValesstraCRO('valesstra_complete_the_look_click', {
        source: 'complete_the_look',
        destination: completeLookLink.getAttribute('href') || null,
        product_path: window.location.pathname,
      });
    }

    const checkoutButton = event.target.closest('#CartDrawer-Checkout, .cart-drawer .cart__checkout-button');
    if (checkoutButton) {
      publishValesstraCRO('valesstra_checkout_click', {
        source: 'cart_drawer',
        product_path: window.location.pathname,
      });
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initValesstraCROTracking, { once: true });
} else {
  initValesstraCROTracking();
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.mountValesstraCrossSell();
    this.mountValesstraProtection();

    this.valesstraCrossSellObserver = new MutationObserver(() => {
      this.mountValesstraCrossSell();
      this.mountValesstraProtection();
    });
    this.valesstraCrossSellObserver.observe(this, { childList: true, subtree: true });
  }

  mountValesstraCrossSell() {
    const crossSell = this.querySelector('[data-valesstra-cart-cross-sell]');
    if (!crossSell) return;

    crossSell.querySelectorAll('[data-valesstra-upsell-variant]').forEach((select) => {
      if (select.dataset.valesstraBound === 'true') return;

      const item = select.closest('.v2-bag-recommendations__item, .valesstra-cart-cross-sell__item');
      const submitButton = item?.querySelector('[data-valesstra-upsell-submit]');
      if (!submitButton) return;

      const syncButton = () => {
        const option = select.selectedOptions[0];
        const variantId = select.value || '';
        submitButton.dataset.variantId = variantId;
        submitButton.disabled = !variantId || option?.disabled === true || option?.dataset.available === 'false';

        if (!variantId || !item?.classList.contains('v2-bag-recommendations__item')) return;
        const mediaLink = item.querySelector('.v2-bag-recommendations__media');
        const titleLink = item.querySelector('.v2-bag-recommendations__title');
        const price = item.querySelector('[data-v2-recommendation-price]');
        const mediaTemplate = Array.from(item.querySelectorAll('template[data-v2-recommendation-media]')).find(
          (template) => template.dataset.v2RecommendationMedia === variantId
        );

        if (mediaLink && mediaTemplate) mediaLink.replaceChildren(mediaTemplate.content.cloneNode(true));
        if (price) {
          price.textContent = option?.dataset.price || '';
          if (option?.dataset.compareAt) {
            const compareAt = document.createElement('s');
            compareAt.textContent = option.dataset.compareAt;
            price.append(' ', compareAt);
          }
        }
        [mediaLink, titleLink].filter(Boolean).forEach((link) => {
          const url = new URL(link.href, window.location.href);
          url.searchParams.set('variant', variantId);
          link.href = `${url.pathname}${url.search}${url.hash}`;
        });
        if (mediaLink) {
          mediaLink.setAttribute('aria-label', `${item.dataset.v2RecommendationTitle} — ${option?.dataset.variantTitle || ''}`);
        }
      };

      select.dataset.valesstraBound = 'true';
      select.addEventListener('change', syncButton);
      syncButton();
    });

    crossSell.querySelectorAll('[data-valesstra-upsell-submit]').forEach((button) => {
      if (button.dataset.valesstraBound === 'true') return;

      button.dataset.valesstraBound = 'true';
      button.addEventListener('click', () => this.addValesstraCrossSell(button));
    });
  }

  mountValesstraProtection() {
    this.querySelectorAll('[data-v2-protection-add]').forEach((button) => {
      if (button.dataset.valesstraBound === 'true') return;
      button.dataset.valesstraBound = 'true';
      button.addEventListener('click', () => this.addValesstraProtection(button));
    });
  }

  async addValesstraProtection(button) {
    const variantId = String(button.dataset.variantId || '');
    const primaryLineKey = button.dataset.primaryLineKey || '';
    const primaryQuantity = Math.max(Number(button.dataset.primaryQuantity) || 1, 1);
    if (!variantId || !primaryLineKey || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    const offer = button.closest('[data-v2-bag-protection]');
    const errorRegion = offer?.querySelector('[data-v2-protection-error]');
    const idleLabel = button.dataset.addLabel || button.textContent.trim();
    if (errorRegion) errorRegion.textContent = '';
    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', String(primaryQuantity));
    formData.append('properties[_valesstra_source]', 'protection');
    formData.append('properties[_valesstra_protected_line_key]', primaryLineKey);
    formData.append('sections', this.getSectionsToRender().map((section) => section.id));
    formData.append('sections_url', window.location.pathname);
    config.body = formData;
    this.setActiveElement(button);

    try {
      const response = await fetch(`${routes.cart_add_url}`, config);
      const parsedState = await response.json();
      if (!response.ok || parsedState.status) {
        throw new Error(parsedState.description || parsedState.message || button.dataset.errorLabel);
      }
      const returnedVariantId = String(parsedState.variant_id || parsedState.id || '');
      if (returnedVariantId !== variantId) throw new Error(button.dataset.errorLabel);
      button.textContent = button.dataset.addedLabel || idleLabel;
      publishValesstraCRO('valesstra_protection_add', {
        source: 'cart_protection',
        variant_id: variantId,
        product_path: window.location.pathname,
      });
      this.renderContents(parsedState);
    } catch (error) {
      console.error(error);
      if (errorRegion) errorRegion.textContent = error.message || button.dataset.errorLabel;
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      button.textContent = idleLabel;
    }
  }

  async addValesstraCrossSell(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.getAttribute('aria-disabled') === 'true') return;

    const recommendation = button.closest('.v2-bag-recommendations__item');
    const errorRegion = recommendation?.querySelector('[data-v2-recommendation-error]');
    const productTitle = recommendation?.querySelector('.v2-bag-recommendations__title')?.textContent?.trim() || null;
    const spinner = button.querySelector('.loading__spinner');
    if (errorRegion) errorRegion.textContent = '';
    button.setAttribute('aria-disabled', 'true');
    button.disabled = true;
    button.classList.add('loading');
    spinner?.classList.remove('hidden');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append('properties[_valesstra_source]', 'cart_cross_sell');
    formData.append(
      'sections',
      this.getSectionsToRender().map((section) => section.id)
    );
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    this.setActiveElement(button);

    try {
      const response = await fetch(`${routes.cart_add_url}`, config);
      const parsedState = await response.json();

      if (parsedState.status) {
        throw new Error(parsedState.description || parsedState.message || 'Unable to add item');
      }
      const returnedVariantId = String(parsedState.variant_id || parsedState.id || '');
      if (returnedVariantId !== String(variantId)) {
        throw new Error('Added item did not match the selected option');
      }

      publishValesstraCRO('valesstra_cart_cross_sell_add', {
        source: 'cart_cross_sell',
        variant_id: variantId,
        product_title: productTitle,
        product_path: window.location.pathname,
      });

      this.renderContents(parsedState);
    } catch (error) {
      console.error(error);
      if (errorRegion) errorRegion.textContent = error.message || window.cartStrings.error;
      button.removeAttribute('aria-disabled');
      button.disabled = false;
      button.classList.remove('loading');
      spinner?.classList.add('hidden');
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.mountValesstraCrossSell();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    document.getElementById('cart-icon-bubble')?.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.getElementById('cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.mountValesstraCrossSell();
      this.mountValesstraProtection();
      const addedStatus = this.querySelector('[data-v2-bag-added-status]');
      if (addedStatus) addedStatus.hidden = false;
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
